import { Dependency } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import {
  ItemDependencyStatus,
  isItemDependencyAtRisk,
  isItemDependencyBlocked,
} from '@/common/api/types/dashboard/health';
import { DependencyType, type ItemDependency } from '@/common/api/types/items';
import { AlertDot } from '@/common/components/badges/AlertDot';
import { useItemPanelNavigation } from '@/features/item-panel/hooks/useItemPanelParams';
import { TabValue } from '@/features/item-panel/types';
import { useVisibleDependencies } from '../context/VisibleDependenciesContext';
import { useVisibleItemHealth } from '../context/VisibleHealthContext';
import type { ItemsTableRow } from '../types';

export const DependencyStatusCell = ({ row }: { row: ItemsTableRow }) => {
  const { t } = useTranslation();
  const { openItemPanel } = useItemPanelNavigation();
  const health = useVisibleItemHealth(row.id);
  const liveDependencies = useVisibleDependencies(row.id);
  const dependencies = liveDependencies ?? row.dependencies;

  // Inherited entries only count when the ancestor is the successor (dependencyType PREDECESSOR).
  const isEligibleDependency = (dependency: ItemDependency) =>
    !dependency.inherited || dependency.dependencyType === DependencyType.Predecessor;

  const eligibleDependencies = dependencies?.filter(isEligibleDependency);

  const hasDeps = (eligibleDependencies?.length ?? 0) > 0;
  const hasPredecessors =
    eligibleDependencies?.some((d) => d.dependencyType === DependencyType.Predecessor) ?? false;

  if (!hasDeps) return null;

  const isCannotEvaluateOnly = (itemHealth: NonNullable<typeof health>) =>
    itemHealth.dependency?.status === ItemDependencyStatus.CANNOT_EVALUATE &&
    itemHealth.dependency.atRiskPredecessorIds.length === 0;

  const dependencyStatus: 'blocked' | 'atRisk' | 'cannotEvaluate' | 'ok' | null = !hasPredecessors
    ? 'ok'
    : health == null
      ? null
      : isItemDependencyBlocked(health)
        ? 'blocked'
        : isCannotEvaluateOnly(health)
          ? 'cannotEvaluate'
          : isItemDependencyAtRisk(health)
            ? 'atRisk'
            : 'ok';

  const statusTooltip =
    dependencyStatus === 'blocked'
      ? t(($) => $.views.table.columns.dependencies.blocked)
      : dependencyStatus === 'atRisk'
        ? t(($) => $.views.table.columns.dependencies.atRisk)
        : dependencyStatus === 'cannotEvaluate'
          ? t(($) => $.views.table.columns.dependencies.cannotEvaluate)
          : dependencyStatus === 'ok'
            ? t(($) => $.views.table.columns.dependencies.ok)
            : null;

  return (
    <TooltipProvider>
      <button
        type="button"
        aria-label={
          statusTooltip
            ? `${t(($) => $.loopVisualization.timelineTooltip.openDependencies)} — ${statusTooltip}`
            : t(($) => $.loopVisualization.timelineTooltip.openDependencies)
        }
        className="flex w-full items-center cursor-pointer"
        onClick={() => openItemPanel({ itemId: row.id, panelTab: TabValue.DEPENDENCIES })}
      >
        {statusTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="relative inline-flex">
                <Dependency className="size-4 text-icon-neutral-secondary" />
                {dependencyStatus !== 'ok' && (
                  <AlertDot
                    variant={dependencyStatus === 'blocked' ? 'error' : 'warning'}
                    data-testid="dependency-alert-dot"
                  />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>{statusTooltip}</TooltipContent>
          </Tooltip>
        ) : (
          <Dependency className="size-4 text-icon-neutral-secondary" />
        )}
      </button>
    </TooltipProvider>
  );
};
